import { useSearchBasicMap } from "@/components/Attributes/BasicMap/useSearchBasicMap";
import MapModal from "@/components/MapModal";
import {
  ApprovalTableBody,
  ApprovalTableHead,
} from "@/components/ProgressList/results/Approval";
import {
  BasicMapTableBody,
  BasicMapTableHead,
} from "@/components/ProgressList/results/BasicMap";
import {
  FaqTableBody,
  FaqTableHead,
} from "@/components/ProgressList/results/Faq";
import { NaviTableBody } from "@/components/ProgressList/results/Navi";
import {
  SalesApplyTableBody,
  SalesApplyTableHead,
} from "@/components/ProgressList/results/SalesApply";
import {
  ServiceStartInfoTableBody,
  ServiceStartInfoTableHead,
  ServiceStartInfoTableHeadAttributes,
} from "@/components/ProgressList/results/ServiceStartInfo";
import {
  SharedTableBodyAttributes,
  SharedTableBodyTabs,
} from "@/components/ProgressList/results/SharedTableBody";
import {
  SharedTableHeadAttributes,
  SharedTableHeadTabNames,
} from "@/components/ProgressList/results/SharedTableHead";
import { useProgressListSearchResults } from "@/components/ProgressList/useProgressListSearchResults";
import { SortIndicator } from "@/components/SortIndicator";
import * as Config from "@/config";
import { CATEGORY_CODE_FAQ, CATEGORY_CODE_SALES_APPLY } from "@/config";
import { SearchResults } from "@/containers/progress/ProgressList";
import { axios } from "@/lib/axios";
import { basicMapStore } from "@/stores/basicMap";
import { useLoadingStore } from "@/stores/loading";
import { useMessageModalStore } from "@/stores/message";
import { useProgressStore } from "@/stores/progress";
import { ProjectCategory } from "@/stores/project";
import { useUserStore } from "@/stores/user";
import * as Common from "@/utils/common";
import { useFixedColumns } from "@/utils/useFixedColumns";
import { useInterval } from "@/utils/UseInterval";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactPaginate from "react-paginate";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";

/**
 * 検索結果
 */
export interface SearchResultsContainerProps {
  areaName: string;
  blockName: string;
  searchResults: SearchResults;
  checkIds: Set<number>;
  onClickCheck: (id: number) => void;
  onClickCheckAll: () => void;
  onPageChange: (page: number) => void;
  selectedTabStatusesId: number | undefined;
  onSelectedTabStatusesId: (tabStatusesId: number) => void;
  selectedTabId: number | undefined;
  onSelectedTabId: (tabId: number) => void;
  selectedProjectCategory: ProjectCategory | undefined;
  onSelectedFaq: () => void;
  onSelectedFaqStatus: (statusId: number) => void;
  officialPositions: any[];
}

export const SearchResultsContainer = (props: SearchResultsContainerProps) => {
  const user = useUserStore((state) => state.user);
  const setLoading = useLoadingStore((state) => state.setLoading);
  const setMessage = useMessageModalStore((state) => state.setMessage);
  const [
    setTabs,
    setListAttributes,
    serviceStartInfoType,
    setServiceInfoSourceAttr,
  ] = useProgressStore((state) => [
    state.setTabs,
    state.setListAttributes,
    state.serviceStartInfoType,
    state.setServiceInfoSourceAttr,
  ]);
  const { getSearchResultBasicMapStatus } = useSearchBasicMap();
  const { listAttributes, getDeliveryDateAttr } =
    useProgressListSearchResults();
  const { certificationStatus, submissionStatus } = basicMapStore();
  const progressTableRef = useRef<HTMLTableElement>(null);
  const [mapModalConfirm, setMapModalConfirm] = useState<
    { callback: () => void; dataId: number } | undefined
  >(undefined);

  const isDrmApply = Config.DRM_APPLY_CATEGORY_IDS.includes(
    props.selectedProjectCategory?.id as number
  );
  const isBasicMap =
    props.selectedProjectCategory?.id === Config.PROJECT_CATEGORY_ID_BASIC_MAP;
  const isSalesApply =
    props.selectedProjectCategory?.category_code ===
      CATEGORY_CODE_SALES_APPLY && !isDrmApply;
  const isFaq =
    props.selectedProjectCategory?.category_code === CATEGORY_CODE_FAQ &&
    !isDrmApply;
  const isServiceStartInfo =
    props.selectedProjectCategory?.id ===
    Config.PROJECT_CATEGORY_ID_SERVICE_START_INFO;
  const isApproval =
    props.selectedProjectCategory?.id === Config.PROJECT_CATEGORY_ID_APPROVAL;
  const isNavi =
    props.selectedProjectCategory?.id === Config.PROJECT_CATEGORY_ID_NAVI;

  const isNotServiceStartInfoProgress =
    serviceStartInfoType !== Config.ServiceStartInformationList.progress;
  const isServiceStartInfoCollect =
    serviceStartInfoType === Config.ServiceStartInformationList.collect;

  // FAQ表示時は、リンク属性もマージ（FAQのリンク属性を使用し、MyPageのIDも保持）
  let linkAttr = props.searchResults.attributes?.[0];
  if (isFaq && props.searchResults.attributes?.length > 0) {
    // FAQとMyPageのリンク属性を探す
    const faqLinkAttr = props.searchResults.attributes.find(
      (attr: any) =>
        attr.is_link &&
        attr.project_category_id === Config.PROJECT_CATEGORY_ID_FAQ
    );
    const myPageLinkAttr = props.searchResults.attributes.find(
      (attr: any) =>
        attr.is_link &&
        attr.project_category_id === Config.PROJECT_CATEGORY_ID_MYPAGE_TICKET
    );

    if (faqLinkAttr && myPageLinkAttr) {
      linkAttr = {
        ...faqLinkAttr,
        myPageAttributeId: myPageLinkAttr.id,
      };
    } else if (faqLinkAttr) {
      linkAttr = faqLinkAttr;
    }
  }

  const linkListWidth = linkAttr?.attribute_setting_values.find(
    (setting_value: any) =>
      setting_value.attribute_setting_code ===
      Config.ATTRIBUTE_SETTING_LIST_WIDTH
  );
  const linkWidth = linkListWidth ? `${linkListWidth.value}px` : "200px";

  const listAttrs = useMemo(() => {
    let attrs = listAttributes(
      props.searchResults.attributes,
      serviceStartInfoType,
      setServiceInfoSourceAttr,
      props.selectedProjectCategory?.id
    );

    // FAQ表示時は、column_nameでグループ化して重複を除去し、MyPageの属性IDも保持
    if (props.selectedProjectCategory?.id === Config.PROJECT_CATEGORY_ID_FAQ) {
      const mergedAttrs = new Map<string, any>();
      const allAttributesByColumnName = new Map<string, any[]>();

      // column_nameごとにすべての属性を収集
      attrs.forEach((attr) => {
        if (!allAttributesByColumnName.has(attr.column_name)) {
          allAttributesByColumnName.set(attr.column_name, []);
        }
        allAttributesByColumnName.get(attr.column_name)!.push(attr);
      });

      // column_nameごとにマージ
      allAttributesByColumnName.forEach((attributeList, columnName) => {
        const faqAttr = attributeList.find(
          (attr) => attr.project_category_id === Config.PROJECT_CATEGORY_ID_FAQ
        );
        const myPageAttr = attributeList.find(
          (attr) =>
            attr.project_category_id ===
            Config.PROJECT_CATEGORY_ID_MYPAGE_TICKET
        );

        if (faqAttr) {
          mergedAttrs.set(columnName, {
            ...faqAttr,
            myPageAttributeId: myPageAttr?.id || null,
            allAttributeIds: attributeList.map((attr) => attr.id), // すべての属性IDを保持
          });
        } else if (myPageAttr) {
          mergedAttrs.set(columnName, {
            ...myPageAttr,
            allAttributeIds: [myPageAttr.id],
          });
        }
      });

      attrs = Array.from(mergedAttrs.values());
    }

    return attrs;
  }, [
    props.searchResults.attributes,
    props.selectedProjectCategory?.id,
    serviceStartInfoType,
  ]);
  const desiredDeliveryDateAttr = getDeliveryDateAttr(
    props.searchResults.attributes
  );

  const approverMembers = props.searchResults.approverMembers;

  const getTabStatusCount = (tab_statuses_id: number) => {
    const count = props.searchResults.dataTabStatusCounts.find(
      (count: any) => count.tab_statuses_id === tab_statuses_id
    );
    return count?.tab_statuses_count ?? 0;
  };

  const allTabCounts = props.searchResults.dataTabStatusCounts?.reduce(
    (accumulator, value) => {
      if (accumulator[value.tab_id]) {
        accumulator[value.tab_id] += value.tab_statuses_count;
      } else {
        accumulator[value.tab_id] = value.tab_statuses_count;
      }
      return accumulator;
    },
    {}
  );

  const allFaqCounts = props.searchResults.data_faq_status_counts?.reduce(
    (accumulator, currentValue) =>
      accumulator + currentValue.faq_statuses_count,
    0
  );

  const currentFaqStatuses = (data_id: number) => {
    return props.searchResults.faq_statuses?.find((item) => {
      return item.id === data_id;
    });
  };

  const [lockedList, setLockedList] = useState<any[]>([]);

  useEffect(() => {
    getLockedList();
  }, []);

  useEffect(() => {
    setListAttributes(listAttrs);
  }, [listAttrs]);

  useEffect(() => {
    setTabs(props.searchResults.tabs);
  }, [props.searchResults.tabs]);

  useInterval(() => {
    getLockedList();
  }, 60 * 1000);

  const getLockedList = () => {
    const dataIds = props.searchResults.datas.map((data: any) => data.id);
    if (dataIds.length == 0) {
      return;
    }
    axios
      .get("/api/v1/datas/lockedList", {
        params: {
          data_id: dataIds,
        },
      })
      .then((result) => {
        setLockedList(result.data.data);
      })
      .catch((error) => {
        setMessage(Config.MESSAGE_NO_E39);
      })
      .finally(() => {});
  };

  const getLink = (project_category_id: any, data_id: any, data?: any) => {
    // MyPageDRM-Chatの場合は専用のリンクを返す
    if (
      project_category_id === Config.PROJECT_CATEGORY_ID_MYPAGE_TICKET &&
      data?.data_tickets?.length > 0
    ) {
      const ticket = data.data_tickets[0];
      return `/customer/ticket/${ticket.customer_id}/${ticket.id}`;
    }

    if (
      Common.canApprove(project_category_id, user) &&
      (isSalesApply || isApproval)
    ) {
      return Common.approveDetailUrl(project_category_id, data_id);
    }
    const canAttribute = Common.canShowAttribute(project_category_id, user);
    if (canAttribute === true) {
      return Common.attributesDetailUrl(project_category_id, data_id);
    } else if (canAttribute) {
      return Common.attributesDetailUrl(
        project_category_id,
        data_id,
        canAttribute.tab_id
      );
    }
    if (Common.canFile(project_category_id, user) > 0)
      return Common.filesListUrl(project_category_id, data_id);
    return "";
  };

  const renderTableHeadAttributes = () => {
    if (isSalesApply) {
      return (
        <SalesApplyTableHead
          desiredDeliveryDateAttr={desiredDeliveryDateAttr}
          approverMembers={approverMembers}
          officialPositions={props.officialPositions}
        />
      );
    }
    if (isBasicMap) {
      return <BasicMapTableHead />;
    }
    if (isFaq) {
      return (
        <FaqTableHead
          selectedProjectCategoryId={props.selectedProjectCategory?.id}
        />
      );
    }
    if (isServiceStartInfo) {
      return <ServiceStartInfoTableHead />;
    }
    if (isApproval) {
      return (
        <ApprovalTableHead
          desiredDeliveryDateAttr={desiredDeliveryDateAttr}
          approverMembers={approverMembers}
          officialPositions={props.officialPositions}
        />
      );
    }
    return (
      <>
        <SharedTableHeadAttributes />
        <SharedTableHeadTabNames />
      </>
    );
  };

  useFixedColumns(progressTableRef);

  const openMapModal = (dataId: number) => {
    setMapModalConfirm({
      callback: () => {},
      dataId: dataId,
    });
  };

  return (
    <>
      <div className="progress-tbl-wrap mt-5">
        <Tooltip id="progress-tooltip" style={{ zIndex: 3 }} />
        <table
          className={`progress-tbl ${isBasicMap && "fixed-columns"}`}
          ref={progressTableRef}
        >
          <thead>
            <tr className="header">
              {/* チェックボックス */}
              <th rowSpan={2}>
                <div className="border-box" />
                <div className="uk-flex uk-flex-center">
                  <input
                    type="checkbox"
                    onChange={props.onClickCheckAll}
                    checked={
                      props.searchResults.datas.length > 0 &&
                      props.checkIds.size === props.searchResults.datas.length
                    }
                  />
                </div>
              </th>
              {/* リンク属性 */}
              <th
                rowSpan={2}
                style={{ minWidth: linkWidth, maxWidth: linkWidth }}
                className="sort-indicator-wrap"
              >
                <div className="border-box" />
                {linkAttr?.name}
                <SortIndicator
                  item_name={Config.ProgressListSortItem.ATTRIBUTES}
                  column_name={linkAttr?.column_name}
                />
              </th>
              {renderTableHeadAttributes()}
            </tr>
            <tr className="header">
              {/* 共通化タブステータス */}
              {!isApproval && !isBasicMap && !isNotServiceStartInfoProgress && (
                <>
                  {props.searchResults.tabs.map((tab: any) => {
                    return (
                      <th
                        key={`table-head-tab-${tab.id}`}
                        className="uk-padding-remove-horizontal"
                      >
                        <div className="border-box" />
                        <div className="status-box">
                          {tab.tab_statuses.map((tabStatus: any) => {
                            return (
                              <div
                                className=""
                                key={`progress-list-tab-status-${tab.id}-${tabStatus.id}`}
                              >
                                {props.selectedTabStatusesId !==
                                tabStatus.id ? (
                                  <a
                                    onClick={() =>
                                      props.onSelectedTabStatusesId(
                                        tabStatus.id
                                      )
                                    }
                                  >
                                    {tabStatus.status}：
                                    {getTabStatusCount(tabStatus.id)}
                                  </a>
                                ) : (
                                  <span>
                                    {tabStatus.status}：
                                    {getTabStatusCount(tabStatus.id)}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                          <div className="">
                            {props.selectedTabId !== tab.id ? (
                              <a onClick={() => props.onSelectedTabId(tab.id)}>
                                全件：{allTabCounts[tab.id]}
                              </a>
                            ) : (
                              <span>全件：{allTabCounts[tab.id]}</span>
                            )}
                          </div>
                        </div>
                      </th>
                    );
                  })}
                </>
              )}
              {/*  公開タブステータスのテーブルヘッダー　*/}
              {isFaq && (
                <th className="uk-padding-remove-horizontal">
                  <div className="border-box" />
                  <div className="status-box">
                    {props.searchResults.data_faq_status_counts?.map(
                      (faqStatus: any) => {
                        return (
                          <div
                            key={`search-result-header-faq-${faqStatus.id}`}
                            className=""
                          >
                            <a
                              onClick={() =>
                                props.onSelectedFaqStatus(faqStatus.id)
                              }
                            >
                              {faqStatus.name}：{faqStatus.faq_statuses_count}
                            </a>
                          </div>
                        );
                      }
                    )}
                    <div className="">
                      <a onClick={() => props.onSelectedFaq()}>
                        全件：{allFaqCounts}
                      </a>
                    </div>
                  </div>
                </th>
              )}
              {isServiceStartInfoCollect && (
                <ServiceStartInfoTableHeadAttributes />
              )}
            </tr>
          </thead>
          <tbody>
            {props.searchResults.datas.map((data: any) => {
              const checked = props.checkIds.has(data.id);
              let linkAttrValue = data.data_attribute_values.find(
                (attrValue: any) => linkAttr.id === attrValue.attribute_id
              );

              // FAQ表示時、リンク属性が見つからない場合はmyPageAttributeIdで検索
              if (isFaq && !linkAttrValue && linkAttr.myPageAttributeId) {
                linkAttrValue = data.data_attribute_values.find(
                  (attrValue: any) =>
                    attrValue.attribute_id === linkAttr.myPageAttributeId
                );
              }
              const link = getLink(
                linkAttrValue?.project_category_id,
                linkAttrValue?.data_id,
                data
              );
              const isLocked =
                lockedList.find((locked) => locked.id === data.id) !==
                undefined;
              const basicMapStatus = getSearchResultBasicMapStatus(
                props.searchResults.basic_map_statuses,
                data.id,
                certificationStatus,
                submissionStatus
              );

              const renderTableBody = () => {
                if (isSalesApply) {
                  return (
                    <SalesApplyTableBody
                      data={data}
                      checked={checked}
                      approverMembers={approverMembers}
                      desiredDeliveryDateAttr={desiredDeliveryDateAttr}
                      officialPositions={props.officialPositions}
                    />
                  );
                }
                if (isBasicMap) {
                  return (
                    <BasicMapTableBody
                      data={data}
                      checked={checked}
                      basicMapStatus={basicMapStatus}
                    />
                  );
                }
                if (isNavi) {
                  return (
                    <NaviTableBody
                      data={data}
                      checked={checked}
                      currentFaqStatuses={currentFaqStatuses}
                      openMapModal={openMapModal}
                    />
                  );
                }
                if (isFaq) {
                  return (
                    <FaqTableBody
                      data={data}
                      checked={checked}
                      currentFaqStatuses={currentFaqStatuses}
                      selectedProjectCategoryId={
                        props.selectedProjectCategory?.id
                      }
                    />
                  );
                }
                if (isServiceStartInfo) {
                  return (
                    <ServiceStartInfoTableBody
                      data={data}
                      checked={checked}
                      openMapModal={openMapModal}
                    />
                  );
                }
                if (isApproval) {
                  return (
                    <ApprovalTableBody
                      data={data}
                      checked={checked}
                      approverMembers={approverMembers}
                      desiredDeliveryDateAttr={desiredDeliveryDateAttr}
                      officialPositions={props.officialPositions}
                    />
                  );
                }
                return (
                  <>
                    <SharedTableBodyAttributes
                      data={data}
                      checked={checked}
                      openMapModal={openMapModal}
                    />
                    <SharedTableBodyTabs data={data} />
                  </>
                );
              };

              return (
                <tr
                  key={`search-results-record-${data.id}`}
                  className={checked ? "selected" : ""}
                >
                  {/* チェックボックス */}
                  <td className={checked ? "selected" : ""}>
                    <div className="border-box" />
                    <div className="uk-flex uk-flex-center">
                      <input
                        type="checkbox"
                        onChange={() => {
                          props.onClickCheck(data.id);
                        }}
                        checked={checked}
                      />
                    </div>
                  </td>
                  {/* リンク属性 */}
                  <td
                    className={"uk-text-left" + (checked ? " selected" : "")}
                    style={{ minWidth: linkWidth, maxWidth: linkWidth }}
                    data-tooltip-id="progress-tooltip"
                    data-tooltip-html={linkAttrValue?.attribute_value}
                  >
                    <div className="border-box" />
                    {link ? (
                      <Link to={link}>{linkAttrValue?.attribute_value}</Link>
                    ) : (
                      <span>{linkAttrValue?.attribute_value}</span>
                    )}
                    {isLocked && (
                      <div>
                        <span className="lock-label">{`${data?.modified_user.last_name}${data?.modified_user.first_name}さんが編集中です`}</span>
                      </div>
                    )}
                  </td>
                  {renderTableBody()}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="uk-text-center mt-5">
        全{props.searchResults.paging.totalCount}件（
        {props.searchResults.paging.currentPage}/{props.searchResults.paging.pageCount}
        ）
      </div>
      <div className="pagination-container">
        <a
          onClick={() => {
            props.onPageChange(1);
          }}
        >
          FIRST
        </a>
        <ReactPaginate
          forcePage={props.searchResults.paging.currentPage - 1} // ReactPaginate→0始まり CakePHP→1始まり
          onClick={(selectedItem: { nextSelectedPage: number | undefined }) => {
            if (selectedItem.nextSelectedPage !== undefined)
              props.onPageChange(selectedItem.nextSelectedPage + 1);
            return false;
          }}
          pageRangeDisplayed={5}
          pageCount={props.searchResults.paging.pageCount}
          renderOnZeroPageCount={null}
          containerClassName="pagination"
          previousLabel="<"
          nextLabel=">"
          breakLabel="..."
        />
        <a
          onClick={() => {
            props.onPageChange(props.searchResults.paging.pageCount);
          }}
        >
          LAST
        </a>
      </div>

      <MapModal
        isShow={mapModalConfirm !== undefined}
        dataId={mapModalConfirm?.dataId ?? 0}
        onCancel={() => {
          setMapModalConfirm(undefined);
        }}
      />
    </>
  );
};
